<!-- src\views\components\AddPlaylist.vue -->
<template>
  <div class="card mb-4">
    <div class="card-header pb-0 d-flex justify-content-between align-items-center">
      <h5>Add Playlist</h5>
    </div>

    <div class="card-body px-3 pt-2 pb-4">
      <form @submit.prevent="addTask" class="container">
        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label for="taskName">Playlist Name</label>
              <input v-model="newEvent.name" type="text" class="form-control" id="taskName" />
              <span v-if="errors.event_name" class="error">{{ errors.event_name }}</span>
            </div>
          </div>

          <!-- MULTI-SELECT BRANCHES DROPDOWN -->
          <div class="col-md-5">
            <div class="form-group">
              <label for="branches">Branches</label>
              <multiselect
                v-model="newEvent.selectedBranches"
                :options="availableBranches"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Select Branches"
                label="branch_name"
                track-by="id"
              ></multiselect>
              <span v-if="errors.selectedBranches" class="error">{{ errors.selectedBranches }}</span>
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group">
              <label for="status">Status</label>
              <select class="form-control" id="status" v-model="newEvent.status" required>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div class="row">
          <!-- EMERGENCY BROADCAST FIELD -->
          <div class="col-md-3">
            <div class="form-group">
              <label for="isEmergency">Emergency Broadcast</label>
              <div class="form-check form-switch mt-1">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="isEmergency" 
                  v-model="newEvent.is_emergency_broadcast" 
                  :true-value="1" 
                  :false-value="0"
                >
                <label class="form-check-label" for="isEmergency">
                  {{ newEvent.is_emergency_broadcast == 1 ? 'Yes' : 'No' }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Button to navigate to Media Gallery -->
        <div class="text-center mt-3">
          <router-link :to="{
            path: '/media-gallery',
            query: {
              selectedMedia: JSON.stringify(selectedMedia),
              event_name: newEvent.name,
              selectedBranches: JSON.stringify(newEvent.selectedBranches),
              status: newEvent.status,
              is_emergency_broadcast: newEvent.is_emergency_broadcast
            }
          }" class="btn btn-success">
            Open Media Gallery
          </router-link>
        </div>

        <span v-if="errors.selectedMedia" class="error">{{ errors.selectedMedia }}</span>

        <!-- Selected Media List -->
        <div v-if="selectedMedia.length" class="selected-media mt-4">
          <h6 class="text-center mb-3">Selected Media</h6>
          <div class="row">
            <div v-for="(media, index) in selectedMedia" :key="index" class="col-md-3">
              <div class="media-card">
                <div class="media-tag" :class="media.type.startsWith('image') ? 'image-tag' : 'video-tag'">
                  {{ media.type.startsWith('image') ? "Image" : "Video" }}
                </div>
                <template v-if="media.type.startsWith('image')">
                  <img :src="media.url" class="media-preview" />
                </template>
                <template v-else-if="media.type.startsWith('video')">
                  <video controls class="media-preview">
                    <source :src="media.url" :type="media.type" />
                  </video>
                </template>
                <span class="remove-btn" @click="removeMedia(index)">×</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12 text-center">
            <button type="submit" class="btn btn-primary mt-3"
              style="background-color: #141727; color:white; border:2px solid #141727">
              Add Playlist
            </button>
          </div>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import axios from 'axios'; 
import Swal from 'sweetalert2';
import { io } from "socket.io-client"; 

export default {
  name: "AddPlaylist",
  components: {
    Multiselect
  },
  data() {
    return {
      socket: null, 
      selectedMedia: [],
      availableBranches: [],
      newEvent: {
        name: "",
        selectedBranches: [],
        status: "1",
        is_emergency_broadcast: 0,
      },
      errors: {}
    };
  },
  mounted() {
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
    
    // Logic to ensure we hit the correct port (3005)
    const baseUrl = process.env.VUE_APP_API_BASE_URL;
    const url = new URL(baseUrl);
    const socketUrl = `${url.protocol}//${url.host}`;

    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000
    });

    this.socket.on('connect', () => {
      console.log("✅ Socket connected:", this.socket.id);
    });

    this.socket.on('connect_error', (err) => {
      console.error("❌ Socket connection error:", err.message);
    });

    this.socket.on('playlist_response', (res) => {
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: res.message || 'Playlist added successfully!',
          confirmButtonText: 'OK'
        }).then(() => {
          this.$router.push('/playlist');
        });
      } else {
        // If validation failed on backend, show the error message
        Swal.fire('Error', res.message || 'Validation failed', 'error');
      }
    });

    // Restore selected media and form data from URL
    const selected = this.$route.query.selectedMedia;
    if (selected) { this.selectedMedia = JSON.parse(selected); }
    const eventName = this.$route.query.event_name;
    if (eventName) { this.newEvent.name = eventName; }
    const selectedBranches = this.$route.query.selectedBranches;
    if (selectedBranches) { this.newEvent.selectedBranches = JSON.parse(selectedBranches); }
    const status = this.$route.query.status;
    if (status) { this.newEvent.status = status; }
    const isEmergency = this.$route.query.is_emergency_broadcast;
    if (isEmergency !== undefined) { this.newEvent.is_emergency_broadcast = parseInt(isEmergency); }
  },
  created() {
    this.getAllBranches();
  },
  methods: {
    removeMedia(index) {
      this.selectedMedia.splice(index, 1);
    },
    getAllBranches() {
      const baseUrl = process.env.VUE_APP_API_BASE_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      axios.get(`${baseUrl}/v1/admin/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(response => {
        this.availableBranches = response.data.map(branch => ({
          id: branch.id,
          branch_name: branch.branch_name
        }));
      })
      .catch(e => console.log('Error fetching branches:', e));
    },
addTask() {
      if (!this.newEvent.name || !this.newEvent.selectedBranches || this.newEvent.selectedBranches.length === 0) {
        this.errors = {
          event_name: this.newEvent.name ? '' : 'Playlist Name is required.',
          selectedBranches: (this.newEvent.selectedBranches && this.newEvent.selectedBranches.length) ? '' : 'At least one Branch must be selected.'
        };
        return; 
      }

      if (this.selectedMedia.length === 0) {
        this.errors.selectedMedia = 'At least one media must be selected.';
        return;
      }

      const branchIds = this.newEvent.selectedBranches.map(b => b.id);
      const playlistData = {
        playlist_name: this.newEvent.name,
        branch_ids: branchIds,
        status: parseInt(this.newEvent.status),
        is_emergency_broadcast: this.newEvent.is_emergency_broadcast,
        media_id: this.selectedMedia.map(media => ({
          id: media.id,
          media_url: media.url,
          type: media.type.startsWith('image') ? '1' : '2'
        })),
      };

      const baseUrl = process.env.VUE_APP_API_BASE_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');

      axios.post(`${baseUrl}/v1/admin/playlist-master/add`, playlistData, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Playlist added successfully!',
          confirmButtonText: 'OK'
        }).then(() => {
          this.$router.push('/playlist');
        });
      })
      .catch(error => {
        const message = error.response?.data?.message || 'Route not found (404) or Server Error';
        Swal.fire('Error', message, 'error');
      });
    

      /* --- ORIGINAL SOCKET EMIT COMMENTED OUT ---
      if (this.socket && this.socket.connected) {
          this.socket.emit('save_playlist_socket', playlistData);
      } else {
          Swal.fire({
            icon: 'error',
            title: 'Connection Error',
            text: 'Socket is not connected. Please check if your server is running on port 3005 and refresh the page.',
          });
      }
      */
    },
  }
};
</script>

<style scoped>
.error {
  color: red;
  font-size: 12px;
}

/* Selected Media Grid */
.selected-media {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

/* Media Card */
.media-card {
  position: relative;
  text-align: center;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  padding: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.media-card:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

/* Media Preview */
.media-preview {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 5px;
}

/* Remove Button */
.remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: red;
  color: white;
  border: none;
  font-size: 16px;
  font-weight: bold;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.3s;
}

.remove-btn:hover {
  background: darkred;
}

/* Media Type Tag */
.media-tag {
  position: absolute;
  bottom: 10px;
  left: 10px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  border-radius: 5px;
  color: white;
}

.image-tag {
  background: rgba(0, 123, 255, 0.8);
}

.video-tag {
  background: rgba(255, 69, 0, 0.8);
}
</style>